import { useNavigate, useParams } from 'react-router-dom';
import { StateMessage } from '../components/StateMessage';
import { usePartenaire } from '../data/queries';
import { useSEO } from '../hooks/useSEO';

/** Normalise a bare domain / handle into a usable href. */
function toHref(raw: string | undefined): string | null {
  if (!raw) return null;
  const v = raw.trim();
  if (!v) return null;
  return /^https?:\/\//i.test(v) ? v : `https://${v}`;
}

export function PartenaireDetail() {
  const navigate = useNavigate();
  const { id = '' } = useParams<{ id: string }>();
  const partenaireQ = usePartenaire(id);
  const p = partenaireQ.data;

  useSEO({
    title: p ? `${p.name} — Partenaire` : 'Partenaire',
    description: p?.tagline || p?.description?.slice(0, 160),
    ogImage: p?.logo,
  });

  if (partenaireQ.isLoading) return <StateMessage variant="loading" />;
  if (partenaireQ.error) return <StateMessage variant="error" message={String(partenaireQ.error)} />;
  if (!p) {
    return (
      <div className="page-detail">
        <p>Partenaire introuvable.</p>
        <button type="button" className="mtc-btn mtc-btn-ghost" onClick={() => navigate('/')}>
          Retour à l'accueil
        </button>
      </div>
    );
  }

  const website = toHref(p.website);
  const instagram = toHref(p.instagramUrl);
  const facebook = toHref(p.facebookUrl);

  return (
    <div className="page-detail">
      <button type="button" className="mtc-back" onClick={() => navigate('/')}>
        ← Accueil
      </button>

      <div className="part-detail">
        <div className="part-detail-logo">
          {p.logo ? (
            <img src={p.logo} alt={p.name} />
          ) : (
            <div className="part-detail-logo-ph">{p.name.charAt(0)}</div>
          )}
        </div>
        <div className="part-detail-body">
          <span className="mtc-eyebrow">Partenaire</span>
          <h1>{p.name}</h1>
          {p.tagline && <p className="part-detail-tagline">{p.tagline}</p>}
          {p.description && (
            <p className="part-detail-desc" style={{ whiteSpace: 'pre-line' }}>
              {p.description}
            </p>
          )}

          <div className="part-detail-meta">
            {p.city && (
              <div className="part-detail-meta-row">
                <span>Ville</span>
                <strong>{p.city}</strong>
              </div>
            )}
            {p.phone && (
              <div className="part-detail-meta-row">
                <span>Téléphone</span>
                <a href={`tel:${p.phone.replace(/\s+/g, '')}`}>{p.phone}</a>
              </div>
            )}
            {p.email && (
              <div className="part-detail-meta-row">
                <span>Email</span>
                <a href={`mailto:${p.email}`}>{p.email}</a>
              </div>
            )}
          </div>

          <div className="part-detail-links">
            {website && (
              <a
                href={website}
                target="_blank"
                rel="noreferrer"
                className="mtc-btn mtc-btn-primary"
              >
                Visiter le site →
              </a>
            )}
            {instagram && (
              <a href={instagram} target="_blank" rel="noreferrer" className="mtc-btn mtc-btn-ghost">
                Instagram
              </a>
            )}
            {facebook && (
              <a href={facebook} target="_blank" rel="noreferrer" className="mtc-btn mtc-btn-ghost">
                Facebook
              </a>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
